import api from 'configs/api'
import moment from 'moment'

interface ScheduleData {
  user_id?: string
  doctor_id?: string
  specialty_id?: string
  specialty_slug?: string
  user_country?: string
  plan_id?: string
  user_time_zone?: string
  plan_associate_id?: string
}

interface FetchAvailableDatesParams {
  month: string
  duration: string
  schedule: ScheduleData
}

interface FetchAvailableTimesParams {
  date: string
  duration: string
  openSlotInAgenda: boolean
  schedule: ScheduleData
  attendanceTime?: string
}

interface CreateSlotParams {
  schedule: ScheduleData
  selectedDate: string
  selectedTime: string
  selectedDuration: string
}

interface ScheduleSingleParams {
  schedule: ScheduleData
  scheduleUUID: string
  selectedDate: string
  selectedTime: string
  selectedDuration: string
  openSlotInAgenda: boolean
}

interface FetchAppointmentsDataParams {
  schedule: ScheduleData
  selectedDate: string
  selectedTime: string
  selectedDuration: string
  openSlotInAgenda: boolean
  availableTimes: any[]
}

interface ConfirmAppointmentsParams {
  schedule: ScheduleData
  scheduleUUID: string
  selectedAppointments: string[]
  appointmentsList: any[]
  selectedDuration: string
}

interface FetchEditAvailableDatesParams {
  schedule: ScheduleData
  month: string
  selectedDuration: string
  weekDates: string[]
}

interface FetchEditAvailableTimesParams {
  schedule: ScheduleData
  date: string
  duration: string
}

interface FetchPendingAppointmentsParams {
  doctorId?: string
  userId?: string
}

export const fetchAvailableDates = async ({
  month,
  duration,
  schedule,
}: FetchAvailableDatesParams) => {
  if (!schedule?.doctor_id || !schedule?.specialty_id) return []

  try {
    const response = await api.post('schedule/v1/schedules/verify-dates', {
      country: schedule?.user_country || 'BR',
      doctor_id: schedule?.doctor_id,
      scheduling_month: month,
      specialty_id: schedule?.specialty_id,
      time: duration,
      plan_id: schedule?.plan_id,
      by_last: 'false',
    })

    return response.data?.days || []
  } catch (error) {
    console.error('Erro ao buscar dias disponíveis:', error)
    return []
  }
}

export const fetchAvailableTimes = async ({
  date,
  duration,
  openSlotInAgenda,
  schedule,
  attendanceTime,
}: FetchAvailableTimesParams) => {
  if (openSlotInAgenda) {
    if (!schedule?.doctor_id) return []
  } else {
    if (!schedule?.doctor_id || !schedule?.specialty_id) return []
  }

  try {
    let response

    if (openSlotInAgenda) {
      const interval = attendanceTime || '30'
      const queryParams = new URLSearchParams({
        scheduling_date: date,
        interval: interval,
        doctor_id: schedule?.doctor_id || '',
        time: duration,
      })
      response = await api.get(
        `schedule/v1/schedules/verify-times-not-created?${queryParams.toString()}`
      )
    } else {
      response = await api.post('schedule/v1/schedules/verify-times', {
        specialty_id: schedule?.specialty_id,
        doctor_id: schedule?.doctor_id,
        time: duration,
        country: schedule?.user_country || 'BR',
        plan_id: schedule?.plan_id,
        by_last: 'false',
        scheduling_date: date,
        time_zone: schedule?.user_time_zone || '',
      })
    }

    const times = openSlotInAgenda
      ? response.data?.times || response.data || []
      : response.data?.times || []

    return times
  } catch (error) {
    console.error('Erro ao buscar horários disponíveis:', error)
    return []
  }
}

export const createSlotInAgenda = async ({
  schedule,
  selectedDate,
  selectedTime,
  selectedDuration,
}: CreateSlotParams) => {
  const [startTime, endTime] = selectedTime.split('-')

  const specialty = schedule?.specialty_slug || ''
  const specialtyName =
    specialty.charAt(0).toUpperCase() + specialty.slice(1).toLowerCase()

  const createSlotResponse = await api.post('schedule/v1/schedules', {
    specialty_id: schedule?.specialty_id,
    specialty_time: parseInt(selectedDuration),
    user_id: schedule?.user_id,
    specialty_name: specialtyName,
    day: selectedDate,
    start_time: startTime,
    end_time: endTime,
    recurrence: false,
    countries: [],
    groups: [],
  })

  const createdSchedule = createSlotResponse.data?.schedule?.[0]
  if (!createdSchedule?.id) {
    throw new Error('Não foi possível criar o slot na agenda')
  }

  return createdSchedule.id
}

export const verifyCredits = async (
  userId: string,
  specialtySlug: string,
  quantity: number,
  isAdditional: boolean = false
) => {
  const response = await api.get(
    `payments/ms/v1/appointments/plan/verify-credits?user_id=${userId}&specialty_slug=${specialtySlug}&quantity=${quantity}&is_additional=${isAdditional}`
  )
  return response.data
}

export const fetchCredits = async (userId: string, specialtySlug: string) => {
  const response = await api.get(
    `payments/ms/v1/appointments/plan/credits?user_id=${userId}&specialty_slug=${specialtySlug}`
  )
  return response.data
}

export const scheduleSingle = async ({
  schedule,
  scheduleUUID,
  selectedDate,
  selectedTime,
  selectedDuration,
  openSlotInAgenda,
}: ScheduleSingleParams) => {
  let slotId = selectedTime

  if (openSlotInAgenda) {
    slotId = await createSlotInAgenda({
      schedule,
      selectedDate,
      selectedTime,
      selectedDuration,
    })
  }

  const calculateQuantity = () => {
    const specialty = schedule?.specialty_slug?.toLowerCase()
    const duration = selectedDuration

    if (specialty === 'psicologo') {
      return duration === '60' ? 2 : 1
    }

    if (specialty === 'clinico-geral') {
      return 1
    }

    if (specialty === 'nutricionista') {
      return 1
    }

    return duration === '30' ? 2 : 1
  }

  const quantity = calculateQuantity()

  const creditsInfo = await fetchCredits(
    schedule?.user_id || '',
    schedule?.specialty_slug || ''
  )

  const isAdditional =
    creditsInfo.credits === 0 && creditsInfo.credits_additional > 0

  const creditsResponse = await verifyCredits(
    schedule?.user_id || '',
    schedule?.specialty_slug || '',
    quantity,
    isAdditional
  )

  const schedulingResponse = await api.post('schedulings/ms/v1/doctor', {
    user_id: schedule?.user_id,
    doctor_id: schedule?.doctor_id,
    slots_id: openSlotInAgenda ? [slotId] : slotId,
    specialty_id: schedule?.specialty_id,
    last_appointment_id: scheduleUUID || '',
    purchase_ids: creditsResponse.purchase_ids,
    subscription_id: creditsResponse.subscription_id || '',
    is_additional: isAdditional,
  })

  return schedulingResponse.data
}

export const fetchAppointmentsData = async ({
  schedule,
  selectedDate,
  selectedTime,
  selectedDuration,
  openSlotInAgenda,
  availableTimes,
}: FetchAppointmentsDataParams) => {
  const creditsResponse = await fetchCredits(
    schedule?.user_id || '',
    schedule?.specialty_slug || ''
  )

  const { credits, credits_additional } = creditsResponse

  const currentDate = selectedDate || moment().format('YYYY-MM-DD')

  let timeToUse = '19:30'
  let selectedTimeDisplay = ''
  const selectedSlotId = selectedTime

  if (selectedTime) {
    if (openSlotInAgenda && selectedTime.includes('-')) {
      timeToUse = selectedTime.split('-')[0]
      selectedTimeDisplay = timeToUse
    } else if (!openSlotInAgenda && availableTimes.length > 0) {
      const selectedTimeSlot = availableTimes.find(
        (time: any) => time?.slot_id === selectedTime
      )
      if (selectedTimeSlot?.time) {
        const timeStr = selectedTimeSlot.time
        selectedTimeDisplay = timeStr
        if (/^\d{2}:\d{2}$/.test(timeStr)) {
          timeToUse = timeStr
        } else {
          const timeMoment = moment(timeStr, [
            'HH:mm',
            'h:mm A',
            'HH:mm:ss',
            'HH:mm:ss.SSS',
          ])
          if (timeMoment.isValid()) {
            timeToUse = timeMoment.format('HH:mm')
          } else {
            const timeMatch = timeStr.match(/(\d{1,2}):(\d{2})/)
            if (timeMatch) {
              const hours = parseInt(timeMatch[1]).toString().padStart(2, '0')
              const minutes = timeMatch[2]
              timeToUse = `${hours}:${minutes}`
            }
          }
        }
      }
    } else if (/^\d{2}:\d{2}$/.test(selectedTime)) {
      timeToUse = selectedTime
      selectedTimeDisplay = selectedTime
    }
  }

  let selectedAppointmentUsesFutureCredit = false

  if (credits > 0) {
    selectedAppointmentUsesFutureCredit = false
  } else if (credits_additional > 0) {
    selectedAppointmentUsesFutureCredit = true
  }

  let selectedSlotIds: string[] = []
  if (!openSlotInAgenda && selectedSlotId) {
    if (Array.isArray(selectedSlotId)) {
      selectedSlotIds = selectedSlotId.filter(Boolean)
    } else {
      selectedSlotIds = [selectedSlotId].filter(Boolean)
    }
  }

  const selectedAppointment = {
    id: selectedSlotId || `selected-${currentDate}-${timeToUse}`,
    date: currentDate,
    time: selectedTimeDisplay || timeToUse,
    slotIds: selectedSlotIds,
    isFutureCredit: selectedAppointmentUsesFutureCredit,
    isSelected: true,
  }

  const remainingCredits = credits > 0 ? credits - 1 : 0
  let remainingAdditionalCredits = credits_additional
  if (credits === 0 && credits_additional > 0) {
    remainingAdditionalCredits = credits_additional - 1
  }

  if (remainingCredits <= 0 && remainingAdditionalCredits <= 0) {
    return [selectedAppointment]
  }

  let currentMonthData: any[] = []
  if (remainingCredits > 0) {
    const currentMonthResponse = await api.get(
      `/doctors/ms/v3/schedules/available-dates?scheduleDate=${currentDate}&scheduleTime=${timeToUse}&credits=${remainingCredits}&professional_id=${schedule?.doctor_id}&specialty_id=${schedule?.specialty_id}&country=${schedule?.user_country || 'BR'}&duration=${selectedDuration}&by_doctor=true`
    )
    currentMonthData = currentMonthResponse.data || []
  }

  let nextMonthStartDate = currentDate
  if (currentMonthData.length > 0) {
    const lastItem = currentMonthData[currentMonthData.length - 1]
    nextMonthStartDate = lastItem.suggestionSchedule?.date || currentDate
  }

  let futureCreditsData: any[] = []
  if (remainingAdditionalCredits > 0) {
    const futureCreditsResponse = await api.get(
      `/doctors/ms/v3/schedules/available-dates?scheduleDate=${nextMonthStartDate}&scheduleTime=${timeToUse}&credits=${remainingAdditionalCredits}&professional_id=${schedule?.doctor_id}&specialty_id=${schedule?.specialty_id}&country=${schedule?.user_country || 'BR'}&duration=${selectedDuration}&is_additional=true&by_doctor=true`
    )
    futureCreditsData = futureCreditsResponse.data || []
  }

  const appointments: any[] = []

  appointments.push(selectedAppointment)

  const normalizeTime = (timeStr: string) => {
    if (!timeStr) return ''
    const timeMoment = moment(timeStr, [
      'HH:mm',
      'h:mm A',
      'HH:mm:ss',
      'HH:mm:ss.SSS',
    ])
    if (timeMoment.isValid()) {
      return timeMoment.format('HH:mm')
    }
    return timeStr
  }

  const normalizedSelectedTime = normalizeTime(selectedTimeDisplay || timeToUse)

  currentMonthData.forEach((item: any) => {
    if (item.suggestionSchedule) {
      const suggestionDate = item.suggestionSchedule.date
      const suggestionTime = item.suggestionSchedule.time
      const normalizedSuggestionTime = normalizeTime(suggestionTime)

      if (
        suggestionDate !== currentDate ||
        normalizedSuggestionTime !== normalizedSelectedTime
      ) {
        appointments.push({
          id:
            item.suggestionSchedule.slotIds?.[0] ||
            `current-${item.suggestionSchedule.date}`,
          date: item.suggestionSchedule.date,
          time: item.suggestionSchedule.time,
          slotIds: item.suggestionSchedule.slotIds || [],
          isFutureCredit: false,
        })
      }
    }
  })

  futureCreditsData.forEach((item: any) => {
    if (item.suggestionSchedule) {
      appointments.push({
        id:
          item.suggestionSchedule.slotIds?.[0] ||
          `future-${item.suggestionSchedule.date}`,
        date: item.suggestionSchedule.date,
        time: item.suggestionSchedule.time,
        slotIds: item.suggestionSchedule.slotIds || [],
        isFutureCredit: true,
      })
    }
  })

  return appointments
}

export const confirmAppointments = async ({
  schedule,
  scheduleUUID,
  selectedAppointments,
  appointmentsList,
  selectedDuration,
}: ConfirmAppointmentsParams) => {
  const calculateQuantity = () => {
    const specialty = schedule?.specialty_slug?.toLowerCase()
    const duration = selectedDuration

    if (specialty === 'psicologo') {
      return duration === '60' ? 2 : 1
    }

    if (specialty === 'clinico-geral') {
      return 1
    }

    if (specialty === 'nutricionista') {
      return 1
    }

    return duration === '30' ? 2 : 1
  }

  const quantityPerAppointment = calculateQuantity()

  const currentMonthAppointments = appointmentsList.filter(
    (appointment) =>
      selectedAppointments.includes(appointment.id) &&
      !appointment.isFutureCredit
  )

  const nextMonthAppointments = appointmentsList.filter(
    (appointment) =>
      selectedAppointments.includes(appointment.id) &&
      appointment.isFutureCredit
  )

  let subscriptionId = schedule?.plan_associate_id || ''

  if (!subscriptionId) {
    try {
      const creditsFallbackResponse = await fetchCredits(
        schedule?.user_id || '',
        schedule?.specialty_slug || ''
      )
      subscriptionId =
        creditsFallbackResponse?.subscription_id ||
        creditsFallbackResponse?.subscription?.id ||
        ''
    } catch (error) {
      console.error('Erro ao buscar subscription_id:', error)
    }
  }

  if (!subscriptionId) {
    throw new Error(
      'Subscription ID não encontrado. Verifique se o usuário possui uma assinatura ativa.'
    )
  }

  const results = []

  if (currentMonthAppointments.length > 0) {
    const currentMonthQuantity =
      currentMonthAppointments.length * quantityPerAppointment

    const currentMonthCreditsResponse = await verifyCredits(
      schedule?.user_id || '',
      schedule?.specialty_slug || '',
      currentMonthQuantity,
      false
    )

    const currentSubscriptionId =
      currentMonthCreditsResponse?.subscription_id || subscriptionId

    const currentMonthSlotsIds = currentMonthAppointments
      .map((appointment) => {
        const slotIds = appointment.slotIds || []
        if (
          Array.isArray(slotIds) &&
          slotIds.length > 0 &&
          Array.isArray(slotIds[0])
        ) {
          return slotIds.flat()
        }
        return slotIds
      })
      .filter((slots) => slots.length > 0)

    if (currentMonthSlotsIds.length === 0) {
      throw new Error('Nenhum slot válido encontrado para o mês atual')
    }

    const currentMonthPayload = {
      user_id: schedule.user_id,
      doctor_id: schedule.doctor_id,
      slots_ids: currentMonthSlotsIds,
      specialty_id: schedule.specialty_id,
      last_appointment_id: scheduleUUID,
      purchase_ids: currentMonthCreditsResponse?.purchase_ids || [],
      subscription_id: currentSubscriptionId,
      is_additional: false,
    }

    const response = await api.post(
      'schedulings/ms/v1/doctor/batch',
      currentMonthPayload
    )
    results.push(response.data)
  }

  if (nextMonthAppointments.length > 0) {
    const nextMonthQuantity =
      nextMonthAppointments.length * quantityPerAppointment

    const nextMonthCreditsResponse = await verifyCredits(
      schedule?.user_id || '',
      schedule?.specialty_slug || '',
      nextMonthQuantity,
      true
    )

    const nextSubscriptionId =
      nextMonthCreditsResponse?.subscription_id || subscriptionId

    const nextMonthSlotsIds = nextMonthAppointments
      .map((appointment) => {
        const slotIds = appointment.slotIds || []
        if (
          Array.isArray(slotIds) &&
          slotIds.length > 0 &&
          Array.isArray(slotIds[0])
        ) {
          return slotIds.flat()
        }
        return slotIds
      })
      .filter((slots) => slots.length > 0)

    if (nextMonthSlotsIds.length === 0) {
      throw new Error('Nenhum slot válido encontrado para o próximo mês')
    }

    const nextMonthPayload = {
      user_id: schedule.user_id,
      doctor_id: schedule.doctor_id,
      slots_ids: nextMonthSlotsIds,
      specialty_id: schedule.specialty_id,
      last_appointment_id: scheduleUUID,
      purchase_ids: nextMonthCreditsResponse?.purchase_ids || [],
      subscription_id: nextSubscriptionId,
      is_additional: true,
    }

    const response = await api.post(
      'schedulings/ms/v1/doctor/batch',
      nextMonthPayload
    )
    results.push(response.data)
  }

  return results
}

export const fetchEditAvailableDates = async ({
  schedule,
  month,
  selectedDuration,
  weekDates,
}: FetchEditAvailableDatesParams) => {
  try {
    const response = await api.post('schedule/v1/schedules/verify-dates', {
      country: schedule?.user_country || 'BR',
      doctor_id: schedule?.doctor_id,
      scheduling_month: month,
      specialty_id: schedule?.specialty_id,
      time: selectedDuration,
      plan_id: schedule?.plan_id,
      by_last: 'false',
    })

    const availableWeekDates = (response.data?.days || []).filter(
      (date: string) => weekDates.includes(date)
    )

    return availableWeekDates
  } catch (error) {
    console.error('Erro ao buscar dias disponíveis para edição:', error)
    return []
  }
}

export const fetchEditAvailableTimes = async ({
  schedule,
  date,
  duration,
}: FetchEditAvailableTimesParams) => {
  try {
    const response = await api.post('schedule/v1/schedules/verify-times', {
      specialty_id: schedule?.specialty_id,
      doctor_id: schedule?.doctor_id,
      time: duration,
      country: schedule?.user_country || 'BR',
      plan_id: schedule?.plan_id,
      by_last: 'false',
      scheduling_date: date,
      time_zone: schedule?.user_time_zone || '',
    })

    return response.data?.times || []
  } catch (error) {
    console.error('Erro ao buscar horários disponíveis para edição:', error)
    return []
  }
}

export const fetchPendingAppointments = async ({
  doctorId,
  userId,
}: FetchPendingAppointmentsParams) => {
  if (!doctorId || !userId) {
    return []
  }

  const doctorIdStr = String(doctorId).trim()
  const userIdStr = String(userId).trim()

  if (!doctorIdStr || !userIdStr || doctorIdStr === '' || userIdStr === '') {
    return []
  }

  if (
    doctorIdStr === 'undefined' ||
    doctorIdStr === 'null' ||
    userIdStr === 'undefined' ||
    userIdStr === 'null'
  ) {
    return []
  }

  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
  if (!uuidRegex.test(doctorIdStr) || !uuidRegex.test(userIdStr)) {
    return []
  }

  try {
    const response = await api.get(
      `schedulings/ms/v1/doctor/${doctorIdStr}/user/${userIdStr}/pending`
    )

    if (response.data && response.data.length > 0) {
      return response.data
    } else {
      return []
    }
  } catch (error: any) {
    if (error?.response?.status === 400) {
      return []
    }

    console.error('Erro ao buscar consultas pendentes:', error)
    return []
  }
}
